/**
 * Flota — weryfikacja tankowań/kosztów i zgłoszenia (`fleet.web.rev.*`): KPI, lightbox, ostrzeżenia, hurtowe zatwierdzanie,
 * skróty klawiaturowe, tablica zgłoszeń. `revEn` jest typowany względem `revPl` — brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetReviewUiPl = {
  rev: {
    filters: 'Filtry',
    lightbox: {
      title: 'Podgląd załącznika: {name}',
      counter: '{n} z {total}',
      zoomIn: 'Powiększ',
      zoomOut: 'Pomniejsz',
      fit: 'Dopasuj do okna',
      download: 'Pobierz plik',
      prev: 'Poprzedni załącznik',
      next: 'Następny załącznik',
      strip: 'Wszystkie załączniki',
      pdfHint: 'Dokument PDF otworzy się w nowej karcie przeglądarki.',
      openPdf: 'Otwórz PDF',
    },
    price: {
      title: 'Cena za litr na tle mediany',
      aria: 'Odchylenie ceny od mediany: {pct}% (mediana {median})',
      median: 'mediana {median}',
      deviation: 'Odchylenie od mediany: {pct}%',
      sample: 'z {count} ostatnich zatwierdzonych tankowań tego paliwa',
      noBaseline: 'Za mało zatwierdzonych tankowań tego paliwa (min. 3), by policzyć medianę.',
    },
    receipt: {
      unavailable: 'Załącznik niedostępny',
      open: 'Otwórz podgląd załączników ({count})',
      zoom: 'Powiększ',
      files: 'Pliki',
    },
    kpi: {
      approx: 'Orientacyjnie: z {shown} najnowszych wpisów z {total}',
      pending: 'Do weryfikacji',
      pendingHint: 'Wpisy czekające na decyzję',
      amount: 'Kwota oczekująca',
      amountHint: 'Suma wpisów do weryfikacji',
      warned: 'Z ostrzeżeniami',
      warnedHint: 'Wszystkie wpisy bez ostrzeżeń',
      noReceipt: 'Bez paragonu: {count}',
      avgPrice: 'Średnia cena paliwa',
      avgPriceHint: 'Ważona litrami, wpisy w kolejce ({count})',
      noDocs: 'Bez dokumentu',
      noDocsHint: 'Wpisy bez załączonego dokumentu',
      topCategory: 'Największa kategoria',
      topCategoryHint: '{pct}% kwoty oczekującej',
    },
    expenses: {
      ringTitle: 'Koszty według kategorii',
      ringHint: 'Udział w kwocie oczekującej',
      ringEmpty: 'Brak kosztów do weryfikacji.',
      ringTotal: 'Razem',
      noDoc: 'Brak dokumentu',
    },
    fuel: {
      summary: 'Podsumowanie tankowania',
      noReceiptNote: 'Brak zdjęcia paragonu — przy włączonym wymogu paragonu zatwierdzenie zostanie odrzucone.',
    },
    shortcuts: { title: 'Skróty', move: 'zaznacz wpis', open: 'otwórz' },
    bulk: {
      button: 'Zatwierdź czyste ({count})',
      title: 'Zatwierdź czyste wpisy',
      description: 'Wpisy zostaną zatwierdzone kolejno, każdy osobną decyzją z wpisem w historii.',
      confirm: 'Zatwierdź {count}',
      summary: '{count} wpisów na kwotę {amount}',
      rulesFuel: 'Czyste = czekają na weryfikację, bez ostrzeżeń, z paragonem i nie są Twoimi własnymi wpisami.',
      rulesExpenses: 'Czyste = czekają na weryfikację, mają załączony dokument i nie są Twoimi własnymi wpisami.',
      skipped: 'Pominięte (ostrzeżenia, brak dokumentu lub własny wpis): {count}.',
      partial: 'Sprawdzono {shown} najnowszych z {total} wpisów w kolejce — pozostałe zweryfikuj ręcznie.',
      progress: 'Zatwierdzono {done} z {total}…',
      resultApproved: 'Zatwierdzono: {count}',
      resultSelf: 'Pominięto własne wpisy (blokada samoakceptacji): {count}',
      resultFailed: 'Nie udało się zatwierdzić: {count} — sprawdź je pojedynczo.',
      resultClean: 'Wszystko poszło bez zastrzeżeń.',
      toastDone: 'Zatwierdzono wpisów: {count}',
    },
    inc: {
      viewBoard: 'Tablica',
      severityTitle: 'Ważność: {label} ({level}/4)',
      missing: 'Brak zdjęć: {count}',
      today: 'dziś',
      age: '{days} dni temu',
      columnEmpty: 'Brak zgłoszeń',
      closedHidden: 'Starsze zamknięte ({count}) są w widoku tabeli.',
      boardPartial: 'Tablica pokazuje {shown} z {total} zgłoszeń — zawęź filtry lub użyj tabeli.',
      decisionBody: 'Zgłoszenie czeka na decyzję od {days} dni. Do czasu decyzji pojazd pozostaje w tym stanie — wybierz, czy może jechać.',
      stripTitle: 'Pojazdy czekające na decyzję: {count}',
      attachProgress: 'Zdjęcia wymagane przez szablon: {have} z {need}',
      attachComplete: 'Komplet',
      gallery: 'Galeria',
      kpi: {
        open: 'Otwarte zgłoszenia',
        openHint: 'Nowe, w obsłudze i rozwiązane',
        decision: 'Czeka na decyzję',
        decisionHint: 'Pojazd bez decyzji o jeździe',
        decisionNone: 'Brak oczekujących decyzji',
        critical: 'Krytyczne otwarte',
        criticalHint: 'Najwyższa ważność',
        incomplete: 'Niekompletne',
        incompleteHint: 'Brakuje zdjęć wymaganych przez szablon',
      },
    },
  },
};

export const fleetReviewUiEn: Shape<typeof fleetReviewUiPl> = {
  rev: {
    filters: 'Filters',
    lightbox: {
      title: 'Attachment preview: {name}',
      counter: '{n} of {total}',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fit: 'Fit to window',
      download: 'Download file',
      prev: 'Previous attachment',
      next: 'Next attachment',
      strip: 'All attachments',
      pdfHint: 'The PDF document opens in a new browser tab.',
      openPdf: 'Open PDF',
    },
    price: {
      title: 'Price per litre vs. median',
      aria: 'Price deviation from median: {pct}% (median {median})',
      median: 'median {median}',
      deviation: 'Deviation from median: {pct}%',
      sample: 'from the last approved fills of this fuel ({count})',
      noBaseline: 'Not enough approved fills of this fuel (min. 3) to compute a median.',
    },
    receipt: {
      unavailable: 'Attachment unavailable',
      open: 'Open attachment preview ({count})',
      zoom: 'Zoom',
      files: 'Files',
    },
    kpi: {
      approx: 'Approximate: from the latest {shown} of {total} entries',
      pending: 'To review',
      pendingHint: 'Entries awaiting a decision',
      amount: 'Pending amount',
      amountHint: 'Sum of entries to review',
      warned: 'With warnings',
      warnedHint: 'No entries with warnings',
      noReceipt: 'No receipt: {count}',
      avgPrice: 'Average fuel price',
      avgPriceHint: 'Litre-weighted, queued entries ({count})',
      noDocs: 'No document',
      noDocsHint: 'Entries without an attached document',
      topCategory: 'Largest category',
      topCategoryHint: '{pct}% of the pending amount',
    },
    expenses: {
      ringTitle: 'Costs by category',
      ringHint: 'Share of the pending amount',
      ringEmpty: 'No costs to review.',
      ringTotal: 'Total',
      noDoc: 'No document',
    },
    fuel: {
      summary: 'Fill-up summary',
      noReceiptNote: 'No receipt photo — if a receipt is required, approval will be rejected.',
    },
    shortcuts: { title: 'Shortcuts', move: 'select entry', open: 'open' },
    bulk: {
      button: 'Approve clean ({count})',
      title: 'Approve clean entries',
      description: 'Entries are approved one by one, each as a separate decision recorded in its history.',
      confirm: 'Approve {count}',
      summary: '{count} entries totalling {amount}',
      rulesFuel: 'Clean = awaiting review, no warnings, with a receipt and not your own entries.',
      rulesExpenses: 'Clean = awaiting review, with an attached document and not your own entries.',
      skipped: 'Skipped (warnings, no document or own entry): {count}.',
      partial: 'Checked the latest {shown} of {total} queued entries — review the rest manually.',
      progress: 'Approved {done} of {total}…',
      resultApproved: 'Approved: {count}',
      resultSelf: 'Skipped own entries (self-approval block): {count}',
      resultFailed: 'Could not approve: {count} — check them one by one.',
      resultClean: 'Everything went through without issues.',
      toastDone: 'Entries approved: {count}',
    },
    inc: {
      viewBoard: 'Board',
      severityTitle: 'Severity: {label} ({level}/4)',
      missing: 'Photos missing: {count}',
      today: 'today',
      age: '{days} days ago',
      columnEmpty: 'No incidents',
      closedHidden: 'Older closed ones ({count}) are in the table view.',
      boardPartial: 'The board shows {shown} of {total} incidents — narrow the filters or use the table.',
      decisionBody: 'This report has been waiting for a decision for {days} days. Until you decide, the vehicle stays in limbo — choose whether it may drive.',
      stripTitle: 'Vehicles awaiting a decision: {count}',
      attachProgress: 'Photos required by the template: {have} of {need}',
      attachComplete: 'Complete',
      gallery: 'Gallery',
      kpi: {
        open: 'Open incidents',
        openHint: 'New, in progress and resolved',
        decision: 'Awaiting decision',
        decisionHint: 'Vehicle without a driving decision',
        decisionNone: 'No pending decisions',
        critical: 'Critical open',
        criticalHint: 'Highest severity',
        incomplete: 'Incomplete',
        incompleteHint: 'Photos required by the template are missing',
      },
    },
  },
};
